import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api';

export const fetchVouchers = createAsyncThunk('vouchers/list', ({ organizersId } = {}) => {
  const params = organizersId ? { organizers_id: organizersId } : undefined;
  return api.get('/vouchers', { params }).then(({ data }) => data.data);
});

export const fetchVoucher = createAsyncThunk('vouchers/details', ({ voucherId }) =>
  api.get(`/vouchers/${voucherId}`).then(({ data }) => data.data)
);

export const createVoucher = createAsyncThunk('vouchers/create', (payload) =>
  api.post('/vouchers', payload).then(({ data }) => data.data)
);

export const updateVoucher = createAsyncThunk('vouchers/update', ({ voucherId, payload }) =>
  api.put(`/vouchers/${voucherId}`, payload).then(({ data }) => data.data)
);

export const updateVoucherStatus = createAsyncThunk('vouchers/status', ({ voucherId, active }) =>
  api.patch(`/vouchers/${voucherId}/status`, { active }).then(({ data }) => data.data)
);

export const fetchVoucherCodes = createAsyncThunk('vouchers/codes/list', ({ voucherId }) =>
  api.get(`/vouchers/${voucherId}/codes`).then(({ data }) => data.data)
);

export const fetchVoucherCodeRedemptions = createAsyncThunk(
  'vouchers/codes/redemptions',
  ({ voucherId, codeId, page = 1, limit = 25 }) =>
    api.get(`/vouchers/${voucherId}/codes/${codeId}/redemptions`, { params: { page, limit } }).then(({ data }) => data)
);

export const createVoucherCode = createAsyncThunk(
  'vouchers/codes/create',
  async ({ voucherId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/vouchers/${voucherId}/codes`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const updateVoucherCode = createAsyncThunk(
  'vouchers/codes/update',
  async ({ voucherId, codeId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/vouchers/${voucherId}/codes/${codeId}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const deleteVoucherCode = createAsyncThunk(
  'vouchers/codes/delete',
  async ({ voucherId, codeId }, { rejectWithValue }) => {
    try {
      await api.delete(`/vouchers/${voucherId}/codes/${codeId}`);
      return codeId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

const initialState = {
  data: [],
  selectedVoucher: null,
  codes: [],
  codeRedemptions: [],
  codeRedemptionsPagination: { page: 1, limit: 25, total: 0 },
  isLoading: false,
  isCodesLoading: false,
  isCodeRedemptionsLoading: false,
  codeRedemptionsRequestId: null,
  error: null,
};

const replaceVoucher = (state, voucher) => {
  state.selectedVoucher = voucher;
  const index = state.data.findIndex(({ id }) => Number(id) === Number(voucher.id));
  if (index >= 0) state.data[index] = voucher;
};

const vouchersSlice = createSlice({
  name: 'vouchers',
  initialState,
  reducers: {
    clearSelectedVoucher: (state) => {
      state.selectedVoucher = null;
      state.codes = [];
      state.codeRedemptions = [];
      state.codeRedemptionsPagination = { page: 1, limit: 25, total: 0 };
      state.codeRedemptionsRequestId = null;
      state.error = null;
    },
    clearVoucherCodeRedemptions: (state) => {
      state.codeRedemptions = [];
      state.codeRedemptionsPagination = { page: 1, limit: 25, total: 0 };
      state.isCodeRedemptionsLoading = false;
      state.codeRedemptionsRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVouchers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVouchers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchVouchers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchVoucher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        replaceVoucher(state, action.payload);
      })
      .addCase(fetchVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(createVoucher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data.unshift(action.payload);
      })
      .addCase(createVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateVoucher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        replaceVoucher(state, action.payload);
      })
      .addCase(updateVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateVoucherStatus.fulfilled, (state, action) => {
        replaceVoucher(state, action.payload);
      })
      .addCase(updateVoucherStatus.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchVoucherCodes.pending, (state) => {
        state.isCodesLoading = true;
        state.error = null;
      })
      .addCase(fetchVoucherCodes.fulfilled, (state, action) => {
        state.isCodesLoading = false;
        state.codes = action.payload;
      })
      .addCase(fetchVoucherCodes.rejected, (state, action) => {
        state.isCodesLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchVoucherCodeRedemptions.pending, (state, action) => {
        state.isCodeRedemptionsLoading = true;
        state.codeRedemptionsRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(fetchVoucherCodeRedemptions.fulfilled, (state, action) => {
        if (state.codeRedemptionsRequestId !== action.meta.requestId) return;
        state.isCodeRedemptionsLoading = false;
        state.codeRedemptionsRequestId = null;
        state.codeRedemptions = action.payload.data;
        state.codeRedemptionsPagination = action.payload.pagination;
      })
      .addCase(fetchVoucherCodeRedemptions.rejected, (state, action) => {
        if (state.codeRedemptionsRequestId !== action.meta.requestId) return;
        state.isCodeRedemptionsLoading = false;
        state.codeRedemptionsRequestId = null;
        state.error = action.error.message;
      })
      .addCase(createVoucherCode.fulfilled, (state, action) => {
        state.codes.unshift(action.payload);
        if (state.selectedVoucher) state.selectedVoucher.code_count = Number(state.selectedVoucher.code_count) + 1;
      })
      .addCase(createVoucherCode.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(updateVoucherCode.fulfilled, (state, action) => {
        const index = state.codes.findIndex(({ id }) => id === action.payload.id);
        if (index >= 0) state.codes[index] = action.payload;
      })
      .addCase(updateVoucherCode.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deleteVoucherCode.fulfilled, (state, action) => {
        state.codes = state.codes.filter(({ id }) => id !== action.payload);
        if (state.selectedVoucher) {
          state.selectedVoucher.code_count = Math.max(0, Number(state.selectedVoucher.code_count) - 1);
        }
      })
      .addCase(deleteVoucherCode.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const { clearSelectedVoucher, clearVoucherCodeRedemptions } = vouchersSlice.actions;
export default vouchersSlice.reducer;
